import { autoUpdate, useClick, useDismiss, useFloating, useInteractions } from "@floating-ui/react";
import { useJsApiLoader } from "@react-google-maps/api";
import { IAddressLite } from "lobbie";
import React, { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { Form, ListGroup } from "react-bootstrap";
import { useLobbieAccountRedux } from "src/hooks/accounts/accounts";
import { useAccountPatientAddresses } from "src/hooks/staff/patients/useAccountPatientAddresses";
import { handleError, isEmptyObject, notify, toAddressString } from "src/utils";
import usePlacesAutocomplete, { getGeocode, getLatLng, getZipCode } from "use-places-autocomplete";
import LobbieSpinner from "../loading/LobbieSpinner";

interface IProps {
    address: IAddressLite | string | undefined;
    setAddress: ((a: IAddressLite | undefined) => void) | null;
    setAddressString: ((a: string | undefined) => void) | null;
    setLoading: (load: boolean) => void;
    name?: string;
    required?: boolean;
    isInvalid?: boolean;
    label?: string | React.ReactNode;
    autoComplete?: string;
    className?: string;
    inputStyle?: React.CSSProperties;
    isReadOnly?: boolean;
}

const SEARCH_OPTIONS = {
    componentRestrictions: { country: ["us", "ca"] },
};

const GOOGLE_MAPS_LIBRARIES = ["places"] as ["places"];

const SuspendedAddressAutocomplete: React.FC<IProps> = (props) => {
    return (
        <Suspense fallback={<LobbieSpinner />}>
            <AddressAutocomplete {...props} />
        </Suspense>
    );
};

const AddressAutocomplete: React.FC<IProps> = ({
    address,
    setAddress,
    setAddressString,
    setLoading,
    name,
    required,
    isInvalid,
    label,
    autoComplete,
    className,
    inputStyle,
    isReadOnly = false,
}) => {
    const { accountPublicUuid } = useLobbieAccountRedux();
    const { reverseGeocode } = useAccountPatientAddresses();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isEditing, setIsEditing] = useState<boolean>(false);

    const {
        init,
        ready,
        value,
        suggestions: { status, data, loading },
        setValue,
        clearSuggestions,
    } = usePlacesAutocomplete({
        initOnMount: false,
        debounce: 300,
        requestOptions: SEARCH_OPTIONS,
    });

    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: import.meta.env.VITE_LOBBIE_GOOGLE_MAPS_API_KEY as string,
        libraries: GOOGLE_MAPS_LIBRARIES,
        preventGoogleFontsLoading: false, // * NOTE!!!! toggling this to true prevents MUI from loading correctly used in FormTypeTime
    });

    const {
        refs: floatingRefs,
        floatingStyles,
        context: floatingContext,
    } = useFloating<HTMLInputElement>({
        placement: "bottom-start",
        open: isEditing,
        // onOpenChange: setIsEditing, this is toggling every click on the reference element. Not desired.
        whileElementsMounted: autoUpdate,
    });
    const floatClick = useClick(floatingContext);
    const floatDismiss = useDismiss(floatingContext);
    const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
        floatClick,
        floatDismiss,
    ]);
    const componentRef = React.useRef<HTMLDivElement>(null);

    // click-outside effect
    const useOnClickOutside = (
        ref: React.RefObject<HTMLElement>,
        callback: (event: MouseEvent | TouchEvent) => void,
        mouseEvent: "mousedown" | "mouseup" = "mousedown",
    ) => {
        useEffect(() => {
            const listener = (event: MouseEvent | TouchEvent) => {
                if (!ref.current || ref.current.contains(event.target as Node)) {
                    return;
                }
                callback(event);
            };
            document.addEventListener(mouseEvent, listener);
            return () => {
                document.removeEventListener(mouseEvent, listener);
            };
        }, [ref, callback, mouseEvent]);
    };

    useOnClickOutside(componentRef, () => {
        setIsEditing(false);
    });

    useEffect(() => {
        if (address) {
            if (typeof address === "string") {
                setValue(address);
            } else {
                setValue(toAddressString(address));
            }
        }
        if (isLoaded && !ready) {
            init();
        }
    }, [isLoaded, init, ready, address, setValue]);

    const handleSetLoading = useCallback(
        (load: boolean) => {
            setLoading(load);
            setIsLoading(load);
        },
        [setLoading, setIsLoading],
    );

    const handleFetchAddress = useCallback(
        (latitude: number, longitude: number) => {
            reverseGeocode(latitude, longitude, accountPublicUuid)
                .then((result) => {
                    handleSetLoading(false);
                    if (isEmptyObject(result) || !result?.address1) {
                        notify({
                            level: "error",
                            title: "Error fetching address.",
                            message: "Please try again.",
                        });
                        return;
                    }
                    if (isEmptyObject(result?.address1)) {
                        notify({
                            level: "error",
                            title: "Invalid address.",
                            message: "Please try again with a more specific address.",
                        });
                        return;
                    }
                    if (setAddress) setAddress(result);
                    setValue(toAddressString(result));
                })
                .catch((error: Error) => {
                    handleSetLoading(false);
                    handleError(error);
                });
        },
        [accountPublicUuid, handleSetLoading, reverseGeocode, setAddress, setValue],
    );

    const handleSelect = useCallback(
        (selectedAddress: string, placeId: string) => {
            if (setAddressString) {
                handleSetLoading(true);
                getGeocode({ placeId })
                    .then((results: google.maps.GeocoderResult[]) => {
                        handleSetLoading(false);

                        const zip = getZipCode(results.first(), false);
                        if (zip && !selectedAddress.includes(zip)) {
                            setAddressString(`${selectedAddress} ${zip}`);
                            setValue(`${selectedAddress} ${zip}`);
                        } else {
                            setAddressString(selectedAddress);
                            setValue(selectedAddress);
                        }
                    })
                    .catch((error: Error) => {
                        handleSetLoading(false);
                        handleError(error);
                        setAddressString(selectedAddress);
                        setValue(selectedAddress);
                    });
            } else if (setAddress) {
                handleSetLoading(true);
                getGeocode({ placeId })
                    .then((results: google.maps.GeocoderResult[]) => getLatLng(results.first()))
                    .then((latLng) => handleFetchAddress(latLng.lat, latLng.lng))
                    .catch((error: Error) => {
                        handleSetLoading(false);
                        handleError(error);
                    });
            } else {
                notify({
                    level: "error",
                    title: "Error selecting address.",
                    message:
                        "This is an issue with Lobbie. Please submit a support request or refresh the page and try again.",
                });
            }
            setIsEditing(false);
            clearSuggestions();
        },
        [
            handleFetchAddress,
            handleSetLoading,
            setAddress,
            setAddressString,
            setValue,
            clearSuggestions,
            setIsEditing,
        ],
    );

    const renderSuggestions = useMemo(() => {
        return (
            <ListGroup className="mt-1">
                {data.map((suggestion) => {
                    const {
                        place_id,
                        description,
                        structured_formatting: { main_text, secondary_text },
                    } = suggestion;

                    return (
                        <ListGroup.Item
                            {...getItemProps({
                                onClick: () => {
                                    handleSelect(description, place_id);
                                },
                            })}
                            key={place_id}
                            className="address-autocomplete-item"
                        >
                            <strong>{main_text}</strong> <small>{secondary_text}</small>
                        </ListGroup.Item>
                    );
                })}
            </ListGroup>
        );
    }, [data, handleSelect, getItemProps]);

    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.target.value === "") {
                if (setAddress) setAddress(undefined);
            }
            setValue(e.target.value);
        },
        [setValue, setAddress],
    );

    return (
        <Form.Group controlId={name ?? "address-autocomplete"} className="m-0" ref={componentRef}>
            {label || null}
            <Form.Control
                name={name ?? "address-autocomplete"}
                className={className}
                autoComplete={autoComplete ?? "off"}
                value={value}
                onChange={handleInputChange}
                required={!!required}
                aria-required={!!required}
                isInvalid={!!loadError}
                disabled={isLoading || isReadOnly}
                isValid={isInvalid === false}
                style={inputStyle}
                readOnly={isReadOnly}
                onFocus={() => {
                    if (!isEditing) {
                        setIsEditing(true);
                    }
                }}
                ref={floatingRefs.setReference}
                {...getReferenceProps()}
            />

            {status === "OK" && (
                <div
                    className="border-right border-left bg-white"
                    style={{ zIndex: 1 }}
                    ref={floatingRefs.setFloating}
                    {...getFloatingProps({ style: floatingStyles })}
                >
                    <div className="text-center" hidden={!(loading || isLoading)}>
                        <LobbieSpinner small />
                    </div>
                    {isEditing && renderSuggestions}
                </div>
            )}
            <Form.Control.Feedback type="invalid">
                {"Please enter a valid address."}
            </Form.Control.Feedback>
        </Form.Group>
    );
};

export default SuspendedAddressAutocomplete;
